import { CorrectiveAction } from "../components/CorrectiveActionsStep";

/**
 * Gets the count of selected corrective actions
 */
export function getSelectedCount(actions: CorrectiveAction[]): number {
	return actions.filter((a) => a.selected).length;
}

/**
 * Gets all selected corrective actions
 */
export function getSelectedActions(
	actions: CorrectiveAction[],
): CorrectiveAction[] {
	return actions.filter((a) => a.selected);
}

/**
 * Validates that at least one action is selected
 */
export function validateActionSelection(actions: CorrectiveAction[]): {
	valid: boolean;
	error?: string;
} {
	const selectedCount = getSelectedCount(actions);

	if (selectedCount === 0) {
		return {
			valid: false,
			error: "Please select at least one corrective action",
		};
	}

	if (selectedCount > 3) {
		return {
			valid: false,
			error: "You can select a maximum of 3 corrective actions",
		};
	}

	return { valid: true };
}

/**
 * Checks if an action can be selected (respects max count)
 */
export function canSelectAction(
	actions: CorrectiveAction[],
	actionId: string,
	maxSelectable: number = 3,
): boolean {
	const action = actions.find((a) => a.id === actionId);
	if (!action) return false;

	// Can always deselect
	if (action.selected) return true;

	// Can select if under max count
	const selectedCount = getSelectedCount(actions);
	return selectedCount < maxSelectable;
}

/**
 * Generates a unique ID for a new action
 */
export function generateActionId(): string {
	return `action_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Creates a new corrective action with default values
 */
export function createNewAction(
	title: string = "New Action",
	description: string = "",
): CorrectiveAction {
	return {
		id: generateActionId(),
		title,
		description,
		selected: false,
		isEditing: false,
	};
}

/**
 * Toggles the selected state of an action
 */
export function toggleAction(
	actions: CorrectiveAction[],
	actionId: string,
	maxSelectable: number = 3,
): CorrectiveAction[] {
	return actions.map((action) => {
		if (action.id === actionId) {
			// Can always deselect
			if (action.selected) {
				return { ...action, selected: false };
			}

			// Can select only if under max count
			const selectedCount = getSelectedCount(actions);
			if (selectedCount < maxSelectable) {
				return { ...action, selected: true };
			}

			// Can't select - already at max
			return action;
		}
		return action;
	});
}

/**
 * Updates an action field
 */
export function updateAction(
	actions: CorrectiveAction[],
	actionId: string,
	field: string,
	value: any,
): CorrectiveAction[] {
	return actions.map((action) => {
		if (action.id === actionId) {
			return { ...action, [field]: value };
		}
		return action;
	});
}

/**
 * Adds a new action to the list
 */
export function addAction(
	actions: CorrectiveAction[],
	newAction: CorrectiveAction,
): CorrectiveAction[] {
	return [...actions, newAction];
}

/**
 * Removes an action from the list
 */
export function removeAction(
	actions: CorrectiveAction[],
	actionId: string,
): CorrectiveAction[] {
	return actions.filter((a) => a.id !== actionId);
}

/**
 * Sanitizes action text (trim, normalize whitespace)
 */
export function sanitizeActionText(text: string): string {
	return text
		.trim()
		.replace(/\s+/g, " ")
		.replace(/\n\s*\n/g, "\n");
}
